import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { Button, Switch, Table, Tag, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { useTrialPlans } from "@/hooks/useTrialPlans";
import { useInvestigatorDirectory } from "@/hooks/usePlannedSites";
import AddInvestigatorModal from "./components/AddInvestigatorModal";
import InvestigatorProfileDrawer, {
  type InvestigatorProfile,
} from "./components/InvestigatorProfileDrawer";
import {
  plannedSiteFromInvestigator,
  resolvePlannedSite,
} from "./plannedSites";
import type { PlannedSite } from "./types";

const TABLE_Y = "calc(100vh - 300px)";

interface Row {
  site: PlannedSite;
  inv: InvestigatorProfile | null;
  included: boolean;
}

/**
 * The applied shortlist for the open scenario, refined by include/exclude.
 * Rows are never deleted here — the list reflects the shortlist algorithm's
 * output — but excluded sites drop out of Site Milestones, Enrollment, and
 * Budget (see activeSites in plannedSites.ts). Manual additions come through
 * the Add Investigator modal and are marked as such.
 */
export default function TrialPlanningSites() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;

  const { byKey: invByKey } = useInvestigatorDirectory();
  const [activeInv, setActiveInv] = useState<InvestigatorProfile | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const rows: Row[] = useMemo(
    () =>
      (plan?.sites ?? []).map((site) => {
        const inv = invByKey.get(site.site_key);
        return {
          site: resolvePlannedSite(site, inv),
          inv: inv ?? null,
          included: site.status !== "rejected",
        };
      }),
    [plan?.sites, invByKey],
  );

  const existingKeys = useMemo(
    () => new Set((plan?.sites ?? []).map((s) => s.site_key)),
    [plan?.sites],
  );

  if (!plan) return null;

  const includedCount = rows.filter((r) => r.included).length;

  const setIncluded = (siteId: string, included: boolean) => {
    updatePlan(plan.id, {
      sites: plan.sites.map((s) =>
        s.id === siteId
          ? { ...s, status: included ? "selected" : "rejected" }
          : s,
      ),
    });
    const site = plan.sites.find((s) => s.id === siteId);
    message.success(
      included
        ? `${site?.investigator_name ?? "Site"} included in planning`
        : `${site?.investigator_name ?? "Site"} excluded — kept on the list, out of downstream planning`,
    );
  };

  const handleManualAdd = (inv: InvestigatorProfile) => {
    const site = plannedSiteFromInvestigator(inv);
    site.status = "selected";
    site.criteria = { shortlist_source: "Manual" };
    updatePlan(plan.id, { sites: [...plan.sites, site] });
    message.success(`${inv.name} added manually`);
  };

  const columns: ColumnsType<Row> = [
    {
      title: "#",
      key: "idx",
      width: 46,
      render: (_, __, i) => (
        <span className="text-[12px] font-semibold text-[#8A97A3] tabular-nums">
          {i + 1}
        </span>
      ),
    },
    {
      title: "Investigator · Site",
      key: "who",
      width: 240,
      render: (_, r) => (
        <div className={r.included ? "" : "opacity-45"}>
          {r.inv ? (
            <button
              type="button"
              onClick={() => setActiveInv(r.inv)}
              className={`font-medium text-[13px] text-[#1F2933] hover:text-[#0C98D0] text-left transition-colors leading-snug ${r.included ? "" : "line-through decoration-[#9CA3AF]"}`}
            >
              {r.inv.name}
            </button>
          ) : (
            <span className="font-medium text-[13px] text-[#1F2933]">
              {r.site.investigator_name}
            </span>
          )}
          <div className="text-[11.5px] text-[#6B7280]">
            {r.site.site_name || "No site on record"}
          </div>
        </div>
      ),
    },
    {
      title: "Location",
      key: "loc",
      width: 150,
      render: (_, r) => (
        <span
          className={`text-[12px] text-[#6B7280] ${r.included ? "" : "opacity-45"}`}
        >
          {[r.site.city, r.site.country].filter(Boolean).join(", ")}
        </span>
      ),
    },
    {
      title: "Experience",
      key: "experience",
      width: 170,
      render: (_, r) => (
        <span
          className={`text-[11.5px] text-[#6B7280] ${r.included ? "" : "opacity-45"}`}
        >
          {r.site.criteria?.shortlist_experience ??
            (r.inv?.trial_count != null ? `${r.inv.trial_count} trials` : "—")}
        </span>
      ),
    },
    {
      title: "Score",
      key: "score",
      width: 84,
      render: (_, r) =>
        r.site.criteria?.shortlist_score ? (
          <span
            className={`text-[13px] font-bold tabular-nums ${r.included ? "" : "opacity-45"}`}
          >
            {r.site.criteria.shortlist_score}
          </span>
        ) : (
          <span className="text-[12px] text-[#D1D5DB]">—</span>
        ),
    },
    {
      title: "Source",
      key: "source",
      width: 190,
      render: (_, r) => {
        const source = r.site.criteria?.shortlist_source ?? "Manual";
        return (
          <Tag
            color={source === "Manual" ? "purple" : "blue"}
            className={`text-[11px] ${r.included ? "" : "opacity-45"}`}
          >
            {source}
          </Tag>
        );
      },
    },
    {
      title: "Included",
      key: "included",
      width: 90,
      align: "center",
      render: (_, r) => (
        <Switch
          size="small"
          checked={r.included}
          aria-label={`Include ${r.site.investigator_name}`}
          onChange={(checked) => setIncluded(r.site.id, checked)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 mb-1 shrink-0">
        <h2 className="text-[15px] font-semibold text-[#1F2933] m-0">
          Refine Site List
        </h2>
        <div className="flex-1" />
        <span className="text-[12px] text-[#6B7280]">
          {includedCount} included · {rows.length - includedCount} excluded
        </span>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="bg-[#0C98D0] border-none"
          onClick={() => setAddOpen(true)}
        >
          Add Investigator
        </Button>
      </div>
      <p className="text-[12.5px] text-[#6B7280] max-w-[64ch] leading-relaxed mb-3 shrink-0">
        The applied shortlist for this scenario. <b>Exclude</b> sites you won't
        carry into planning — the row stays, because the list reflects the
        algorithm's output. Excluded sites drop out of Site Milestones,
        Enrollment, and Budget.
      </p>

      {rows.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center rounded-[8px] border border-dashed border-[#D1D5DB] bg-white">
          <p className="text-[14px] text-[#6B7280] text-center px-6">
            Nothing here yet — save a report on the Shortlist with "apply to
            plan", or add investigators manually.
          </p>
        </div>
      ) : (
        <div className="flex-1 min-h-0 rounded-[8px] border border-[#F0F2F4] bg-white overflow-hidden">
          <Table<Row>
            dataSource={rows}
            columns={columns}
            rowKey={(r) => r.site.id}
            size="small"
            scroll={{ x: "max-content", y: TABLE_Y }}
            pagination={{ pageSize: 25, size: "small" }}
          />
        </div>
      )}

      <AddInvestigatorModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        existingKeys={existingKeys}
        onAdd={handleManualAdd}
      />
      <InvestigatorProfileDrawer
        profile={activeInv}
        onClose={() => setActiveInv(null)}
      />
    </div>
  );
}
